import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';  // <-- import for links
import Config from '../config.json';
import LoginCheck from '../pages/logincheck';

/**
 * LoanVerificationDealerFiles
 * 
 * This page displays a list of dealer files fetched from the loanverification/dealerfilelist endpoint.
 * 
 * Features:
 * - Displays dealer files in a table with details like dealer name, file name, and status
 * - Provides links to view detailed information for each file
 * - Includes functionality to delete files with confirmation modal
 * - Supports uploading new dealer files via navigation link
 */

const DealerFiles = () => {
    const [dealerFiles, setDealerFiles] = useState([]);      // Stores the list of files
    const [errorMessage, setErrorMessage] = useState('');    // Stores any error message
    const [statusMessage, setStatusMessage] = useState('');  // Stores a loading or status message

    // Add a state variable for the confirmation modal
    const [showModal, setShowModal] = useState(false);
    // Add a state variable to store the ID of the file to be deleted
    const [fileToDelete, setFileToDelete] = useState(null);

    // Dynamically decide which base URLs to call depending on the hostname
    const getBaseURL = window.location.hostname === '192.168.1.45'
        ? 'http://localhost:8383/loanverification'
        : 'https://api.galeforceai.com/loanverification';

    // Base URL for system endpoints (different from loanverification endpoints)
    const getSystemBaseURL = window.location.hostname === '192.168.1.45'
        ? 'http://localhost:8383/system'
        : 'https://api.galeforceai.com/system';

    useEffect(() => {
        // Fetch the dealer files on component mount
        const fetchDealerFiles = async () => {
            setStatusMessage('Loading dealer files...');
            try {
                const response = await fetch(`${getBaseURL}/dealerfilelist`, {
                    method: 'GET',
                    headers: {
                        // If your backend requires an Authorization header, include it here.
                        'Authorization': Config.loanverificationrequest
                    }
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch dealer files');
                }
                const data = await response.json();
                setDealerFiles(data);
                setStatusMessage('');
            } catch (error) {
                setErrorMessage(error.message);
                setStatusMessage('');
            }
        };

        fetchDealerFiles();
    }, [getBaseURL]);

    // Handler for downloading a dealer file
    const handleDownloadFile = async (fileId, fileName) => {
        setStatusMessage(`Generating download link for ${fileName}...`);
        
        // Debug: Check what Config.loanverificationrequest contains
        console.log('Config.loanverificationrequest:', Config.loanverificationrequest);
        console.log('Type of Config.loanverificationrequest:', typeof Config.loanverificationrequest);
        
        try {
            const requestBody = {
                file_id: fileId,
                api_key: 'f8aa3f2b-bc46-4ce8-ab48-b7574344a9dd'
            };
            
            console.log('Request body:', requestBody);
            
            const response = await fetch(`${getSystemBaseURL}/generatedownloadurl`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                console.error('Download API Error Status:', response.status);
                console.error('Download API Error Response:', errorData);
                throw new Error(`Failed to generate download URL for ${fileName}: ${response.status} - ${errorData}`);
            }

            const data = await response.json();
            
            if (data.download_url) {
                window.open(data.download_url, '_blank');
                setStatusMessage(`Download started for ${fileName}`);
                setTimeout(() => setStatusMessage(''), 3000);
            } else {
                throw new Error('No download URL received');
            }
        } catch (error) {
            setErrorMessage(`Download failed: ${error.message}`);
            setStatusMessage('');
        }
    };

    // Handler for deleting a dealer file
    const handleDeleteFile = async (fileId) => {
        setStatusMessage(`Deleting file with ID: ${fileId}...`);
        try {
            const response = await fetch(`${getBaseURL}/dealerfiledelete/${fileId}`, {
                method: 'POST',
                headers: {
                    'Authorization': Config.loanverificationrequest
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to delete file with ID: ${fileId}`);
            }
            // Remove the deleted file from the state
            setDealerFiles((prevFiles) => prevFiles.filter((file) => file._id !== fileId));
            setStatusMessage(`File with ID: ${fileId} deleted successfully.`);
        } catch (error) {
            setErrorMessage(error.message);
            setStatusMessage('');
        }
    };

    // Function to open the modal and set the file ID to be deleted
    const openModal = (fileId) => {
        setFileToDelete(fileId);
        setShowModal(true);
    };

    // Function to close the modal
    const closeModal = () => {
        setFileToDelete(null);
        setShowModal(false);
    };

    // Function to confirm the file deletion
    const confirmDelete = () => {
        if (fileToDelete) {
            handleDeleteFile(fileToDelete);
        }
        closeModal();
    };

    return (
        <div>
            <LoginCheck />

            <h1 className="page-header">
                Dealer Files
            </h1>

            {statusMessage && (
                <div style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                    {statusMessage}
                </div>
            )}

            {errorMessage && (
                <div style={{ marginBottom: '1rem', color: 'red' }}>
                    {errorMessage}
                </div>
            )}

            {/* A simple table layout for the fetched dealer files. Adjust columns as needed. */}
            <table style={{ borderCollapse: 'collapse', width: '80%' }}>
                <thead>
                    <tr>
                        <th style={{ border: '1px solid #ccc', padding: '8px' }}>Dealer Name</th>
                        <th style={{ border: '1px solid #ccc', padding: '8px' }}>File Name</th>
                        <th style={{ border: '1px solid #ccc', padding: '8px' }}>Upload Date</th>
                        <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Delete</th>
                    </tr>
                </thead>
                <tbody>
                    {dealerFiles.map((file, index) => (
                        <tr key={index}>
                            <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                {/* Link to detail page, using _id */}
                                <Link to={`/loanverification/dealerfiles/${file._id}`}>
                                    {file.dealerName}
                                </Link>
                            </td>
                            <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                {/* Download link for the file */}
                                <button
                                    onClick={() => handleDownloadFile(file._id, file.fileName)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#007bff',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                >
                                    {file.fileName}
                                </button>
                            </td>
                            <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                {file.uploadDate}
                            </td>
                            <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                {/* Delete button */}
                                <button 
                                    className="btn btn-danger"
                                    onClick={() => {
                                        // Show confirmation modal when delete is clicked.
                                        openModal(file._id);
                                    }}
                                    style={{
                                        display: 'block',
                                        margin: '0 auto'
                                    }}>
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={{ marginTop: '1rem' }}>
                <Link to='/loanverification/dealerfileupload'>
                    <button className="btn btn-primary">Upload New Dealer File</button>
                </Link>
            </div>

            {/* Custom Modal for Delete Confirmation */}
            {showModal && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000  // Ensure it's on top of other content
                    }}
                    onClick={closeModal} // Close modal when clicking the backdrop
                >
                    <div 
                        style={{
                            backgroundColor: '#f0f0f0',
                            padding: '20px',
                            borderRadius: '5px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
                            minWidth: '300px', // Set a minimum width
                            color: '#000000'
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent clicks on modal content from closing it
                    >
                        <h3 style={{ marginTop: 0, color: '#000000' }}>Confirm Delete</h3>
                        <p style={{ color: '#000000' }}>Are you sure you want to delete this file?</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button 
                                className="btn btn-secondary" 
                                onClick={closeModal}
                                style={{ marginRight: '10px' }}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger" 
                                onClick={confirmDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DealerFiles;
